import { z } from 'zod'
import { WorkerSchema } from './workers.js'

// TimesheetEntry Schema
export const TimesheetEntrySchema = z.object({
  id: z.number().int().positive(),
  timesheetId: z.number().int().positive(),
  workerId: z.number().int().positive(),
  worker: WorkerSchema.optional(), // Optional for populated joins
  timeIn: z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, 'Time in must be in HH:MM format'),
  timeOut: z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, 'Time out must be in HH:MM format'),
  hoursWorked: z.number().min(0, 'Hours worked must be non-negative').max(24, 'Hours worked cannot exceed 24'),
  createdAt: z.string().datetime('Created at must be valid ISO date')
})

// JobSite Schema (minimal)
export const JobSiteSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1, 'Job site name is required'),
  address: z.string().nullable(),
  active: z.boolean(),
  createdAt: z.string().datetime('Created at must be valid ISO date')
})

// DailyTimesheet Schema - Main response schema
export const DailyTimesheetSchema = z.object({
  id: z.number().int().positive(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  payPeriodId: z.number().int().positive(),
  jobSiteId: z.number().int().positive(),
  jobSite: JobSiteSchema.optional(), // Optional for populated joins
  status: z.enum(['draft', 'submitted', 'approved', 'rejected']),
  totalWorkers: z.number().int().min(0, 'Total workers must be non-negative'),
  totalHours: z.number().min(0, 'Total hours must be non-negative'),
  scanFileUrl: z.string().url('Scan file URL must be valid').nullable(),
  submittedBy: z.number().int().positive().nullable(),
  submittedAt: z.string().datetime('Submitted at must be valid ISO date').nullable(),
  version: z.number().int().positive(),
  entries: z.array(TimesheetEntrySchema),
  // Draft management fields
  lastSavedAt: z.string().datetime('Last saved at must be valid ISO date').nullish(),
  saveCount: z.number().int().min(0, 'Save count must be non-negative').nullish(),
  isDraft: z.boolean().nullish(),
  createdAt: z.string().datetime('Created at must be valid ISO date'),
  updatedAt: z.string().datetime('Updated at must be valid ISO date'),
  // Submission workflow fields
  submissionId: z.string().nullish(),
  overtimeHours: z.number().min(0).nullish(),
  estimatedPayroll: z.number().min(0).nullish(),
  approvedBy: z.number().int().positive().nullish(),
  approvedAt: z.string().datetime('Approved at must be valid ISO date').nullish(),
  rejectionReason: z.string().nullish(),
  managementNotes: z.string().nullish()
})

// Timesheet Submit Request Schema
export const TimesheetSubmitRequestSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  payPeriodId: z.number().int().positive().optional(),
  jobSiteId: z.number().int().positive().optional(),
  entries: z.array(z.object({
    workerId: z.number().int().positive(),
    timeIn: z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, 'Time in must be in HH:MM format'),
    timeOut: z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, 'Time out must be in HH:MM format')
  })).min(1, 'At least one timesheet entry is required'),
  scanFileUrl: z.string().url('Scan file URL must be valid').optional(),
  status: z.enum(['draft', 'submitted']).default('draft')
})

// Timesheet Submit Response Schema
export const TimesheetSubmitResponseSchema = z.object({
  timesheet: DailyTimesheetSchema,
  message: z.string().optional()
})

// Timesheet Query Schema for filtering
export const TimesheetQuerySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format').optional(),
  status: z.enum(['draft', 'submitted', 'approved']).optional(),
  payPeriodId: z.number().int().positive().optional(),
  jobSiteId: z.number().int().positive().optional()
})

// Draft and History Management Schemas (from specification)

// Timesheet History Item Schema
export const TimesheetHistoryItemSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  status: z.enum(['draft', 'submitted', 'approved', 'rejected']),
  totalWorkers: z.number().int().min(0),
  totalHours: z.number().min(0),
  lastSavedAt: z.string().datetime().nullable(),
  submittedAt: z.string().datetime().nullable()
})

// Timesheet History Response Schema
export const TimesheetHistoryResponseSchema = z.object({
  timesheets: z.array(TimesheetHistoryItemSchema),
  total: z.number().int().min(0)
})

// Timesheet Draft Save Request Schema
export const TimesheetDraftSaveRequestSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  entries: z.array(z.object({
    workerId: z.number().int().positive(),
    timeIn: z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, 'Time in must be in HH:MM format'),
    timeOut: z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, 'Time out must be in HH:MM format')
  })).min(1, 'At least one timesheet entry is required'),
  payPeriodId: z.number().int().positive().optional(),
  jobSiteId: z.number().int().positive().optional()
})

// Timesheet Draft Save Response Schema
export const TimesheetDraftSaveResponseSchema = z.object({
  timesheet: DailyTimesheetSchema,
  message: z.string().optional()
})

// Copy Workers Request Schema
export const CopyWorkersRequestSchema = z.object({
  fromDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'From date must be in YYYY-MM-DD format'),
  toDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'To date must be in YYYY-MM-DD format'),
  workerIds: z.array(z.number().int().positive()).optional()
})

// Copy Workers Response Schema
export const CopyWorkersResponseSchema = z.object({
  success: z.boolean(),
  workersAdded: z.number().int().min(0),
  message: z.string().optional()
})

// NEW SCHEMAS FOR SUBMISSION WORKFLOW

// Submission Validation Error Schema
export const SubmissionValidationErrorSchema = z.object({
  field: z.string(),
  workerId: z.number().int().positive().optional(),
  message: z.string(),
  severity: z.enum(['error', 'warning'])
})

// Submission Validation Result Schema
export const SubmissionValidationResultSchema = z.object({
  valid: z.boolean(),
  errors: z.array(SubmissionValidationErrorSchema),
  warnings: z.array(SubmissionValidationErrorSchema)
})

// Enhanced Timesheet History Item Schema (extends existing)
export const TimesheetHistoryItemEnhancedSchema = TimesheetHistoryItemSchema.extend({
  overtimeHours: z.number().min(0).optional(),
  submittedBy: z.string().optional(),
  submissionId: z.string().optional(),
  canEdit: z.boolean(),
  canView: z.boolean(),
  hasAttachments: z.boolean()
})

// Reports Query Schema
export const ReportsQuerySchema = z.object({
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  employee: z.string().optional(),
  jobSite: z.string().optional(),
  status: z.enum(['submitted', 'approved', 'rejected']).optional(),
  limit: z.coerce.number().int().positive().max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0)
})

// Reports Response Schema
export const ReportsResponseSchema = z.object({
  timesheets: z.array(TimesheetHistoryItemEnhancedSchema),
  total: z.number().int().min(0),
  hasMore: z.boolean(),
  page: z.number().int().min(0),
  limit: z.number().int().positive()
})

// Timesheet Approval Request Schema
export const TimesheetApprovalRequestSchema = z.object({
  submissionId: z.string().min(1),
  action: z.enum(['approve', 'reject']),
  notes: z.string().optional(),
  approvedBy: z.string().min(1)
})

// Timesheet Approval Response Schema
export const TimesheetApprovalResponseSchema = z.object({
  success: z.boolean(),
  timesheet: DailyTimesheetSchema,
  message: z.string().optional()
})

// Export TypeScript types
export type TimesheetEntry = z.infer<typeof TimesheetEntrySchema>
export type JobSite = z.infer<typeof JobSiteSchema>
export type DailyTimesheet = z.infer<typeof DailyTimesheetSchema>
export type TimesheetSubmitRequest = z.infer<typeof TimesheetSubmitRequestSchema>
export type TimesheetSubmitResponse = z.infer<typeof TimesheetSubmitResponseSchema>
export type TimesheetQuery = z.infer<typeof TimesheetQuerySchema>

// Draft and History Management Types
export type TimesheetHistoryItem = z.infer<typeof TimesheetHistoryItemSchema>
export type TimesheetHistoryResponse = z.infer<typeof TimesheetHistoryResponseSchema>
export type TimesheetDraftSaveRequest = z.infer<typeof TimesheetDraftSaveRequestSchema>
export type TimesheetDraftSaveResponse = z.infer<typeof TimesheetDraftSaveResponseSchema>
export type CopyWorkersRequest = z.infer<typeof CopyWorkersRequestSchema>
export type CopyWorkersResponse = z.infer<typeof CopyWorkersResponseSchema>

// Submission Workflow Types
export type SubmissionValidationError = z.infer<typeof SubmissionValidationErrorSchema>
export type SubmissionValidationResult = z.infer<typeof SubmissionValidationResultSchema>
export type TimesheetHistoryItemEnhanced = z.infer<typeof TimesheetHistoryItemEnhancedSchema>
export type ReportsQuery = z.infer<typeof ReportsQuerySchema>
export type ReportsResponse = z.infer<typeof ReportsResponseSchema>
export type TimesheetApprovalRequest = z.infer<typeof TimesheetApprovalRequestSchema>
export type TimesheetApprovalResponse = z.infer<typeof TimesheetApprovalResponseSchema>

// Note: Business logic functions moved to src/lib/timesheet-business-rules.ts
// This file focuses only on data contracts and schemas 